import {
  CallTaskForm,
  DAYS,
  ROLES_MAX_LENGTH,
  rolesError,
} from './callTaskForm';
import { Select } from './styles';
import { OUTCOME_LABELS } from '../shared/labels';

import Button from '@/components/Button';
import { Body, Small } from '@/components/Typography';
import { Assignee } from '@/services/lifecycleAPI';
import styled from '@/styles';

const Field = styled('label', {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  fontSize: '13px',
  color: '#3D3D3D',
  '& > span:first-child': { fontWeight: 600 },
});

const Inline = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px 24px',
  alignItems: 'center',
});

const Input = styled('input', {
  font: 'inherit',
  fontSize: '14px',
  border: '1px solid #BDBDBD',
  borderRadius: '6px',
  padding: '8px 10px',
  width: '100%',
});

const Notes = styled('textarea', {
  font: 'inherit',
  fontSize: '14px',
  border: '1px solid #BDBDBD',
  borderRadius: '6px',
  padding: '8px 10px',
  width: '100%',
});

const DayButton = styled('button', {
  font: 'inherit',
  fontSize: '13px',
  border: '1px solid #BDBDBD',
  borderRadius: '6px',
  backgroundColor: '#FFFFFF',
  padding: '6px 10px',
  cursor: 'pointer',
  minWidth: '48px',
  '&[aria-pressed="true"]': {
    backgroundColor: '#E7F4E6',
    borderColor: '#1E8E3E',
    color: '#17722F',
    fontWeight: 600,
  },
  '&:disabled': { opacity: 0.55, cursor: 'default' },
});

type Props = {
  form: CallTaskForm;
  setField: <K extends keyof CallTaskForm>(
    key: K,
    value: CallTaskForm[K],
  ) => void;
  assignees: Assignee[];
  assigneesError: string | null;
  myUserId: number | null;
  isFinal: boolean;
  assignedId: number | null;
  assignedName: string | null;
  busy: boolean;
};

const CallTaskFields = ({
  form,
  setField,
  assignees,
  assigneesError,
  myUserId,
  isFinal,
  assignedId,
  assignedName,
  busy,
}: Props) => (
  <>
    <Inline css={{ alignItems: 'flex-start' }}>
      <Field css={{ flex: '1 1 260px' }}>
        <span>
          Outcome <Small color="light">(required to complete)</Small>
        </span>
        <Select
          data-testid="callTaskOutcome"
          disabled={isFinal || busy}
          value={form.outcome}
          onChange={(e) => setField('outcome', e.target.value)}
        >
          <option value="">Choose an outcome</option>
          {Object.entries(OUTCOME_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </Field>
      <Field css={{ flex: '1 1 260px' }}>
        <span>Assigned to</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            data-testid="callTaskAssignee"
            css={{ flex: 1 }}
            disabled={busy}
            value={form.assignee}
            onChange={(e) => setField('assignee', e.target.value)}
          >
            <option value="">Nobody</option>
            {assignedId !== null &&
              !assignees.some((person) => person.user_id === assignedId) && (
                <option value={String(assignedId)}>
                  {`${assignedName ?? `User ${assignedId}`} (not in the list)`}
                </option>
              )}
            {assignees.map((person) => (
              <option key={person.user_id} value={String(person.user_id)}>
                {person.user_id === myUserId
                  ? `${person.name} (you)`
                  : person.name}
              </option>
            ))}
          </Select>
          {myUserId !== null && (
            <Button
              a11yLabel="Assign to me"
              appearance="plain"
              size="sm"
              label="Assign to me"
              disabled={busy}
              onClick={() => setField('assignee', String(myUserId))}
            />
          )}
        </span>
        {assigneesError && <Small color="danger">{assigneesError}</Small>}
      </Field>
    </Inline>
    <Field>
      <span>Preferred roles</span>
      <Input
        disabled={busy}
        placeholder="For example: Server, Barback"
        maxLength={ROLES_MAX_LENGTH}
        value={form.roles}
        onChange={(e) => setField('roles', e.target.value)}
      />
      {rolesError(form.roles) && (
        <Small color="danger" data-testid="callTaskRolesError">
          {rolesError(form.roles)}
        </Small>
      )}
    </Field>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Body weight="medium" id="callTaskDaysLabel">
        Preferred days
      </Body>
      <div
        role="group"
        aria-labelledby="callTaskDaysLabel"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
      >
        {DAYS.map((day) => {
          const on = form.days.includes(day);
          return (
            <DayButton
              key={day}
              type="button"
              aria-pressed={on}
              disabled={busy}
              onClick={() =>
                setField(
                  'days',
                  on ? form.days.filter((d) => d !== day) : [...form.days, day],
                )
              }
            >
              {day}
            </DayButton>
          );
        })}
      </div>
    </div>
    <Field>
      <span>Notes</span>
      <Notes
        data-testid="callTaskNotes"
        rows={3}
        disabled={busy}
        value={form.notes}
        onChange={(e) => setField('notes', e.target.value)}
      />
    </Field>
  </>
);

export default CallTaskFields;
